import { supabase } from "../supabase";
import {
  deactivateSaleReceivable,
  syncSaleClientDebit,
} from "./salesReceivables";
import { buildRestorableQuantityMap } from "./comboStock";
import { SALE_STATUS_REVISION } from "./mpPoint.js";
import { CANCELLED_SALE_STATUSES } from "./saleStatuses.js";
import { restoreVariantStock, splitVariantItems, fetchCambiosTalle, redistribuirPorCambiosTalle } from "./variantStock.js";
import { devolverVentaALotes } from "./stockLotes.js";

// =============================================================================
// Anulación de venta con reversión COMPLETA de efectos colaterales.
// =============================================================================
// Antes, anular una venta solo revertía la cuenta corriente y desactivaba el
// receivable. Quedaban "colgados":
//   - el stock descontado (los productos no volvían),
//   - el Ingreso en movimientos_financieros (libro diario sobreestimado),
//   - el saldo del banco si la venta acreditaba en el momento.
//
// Este módulo centraliza la anulación para VentasHistorial y VentaDetalle.
//
// Qué NO toca (a propósito):
//   - movimientos_caja: el arqueo calcula las ventas desde `sales` (que ya
//     excluye anuladas) e ignora movimientos de categoría 'Venta'. Insertar un
//     egreso acá produciría DOBLE descuento en el teórico.
//   - Ventas facturadas: el guard es responsabilidad de la pantalla (requieren
//     nota de crédito ARCA, no anulación).
//
// Limitación conocida: las actualizaciones son leer-calcular-escribir desde el
// cliente (igual que el resto del módulo de ventas). Lo robusto a futuro es un
// RPC transaccional estilo `registrar_compra`.

// Definición única compartida con flujoFondos.js. Antes había un Set duplicado
// acá y los dos divergieron: 'cobro_en_revision' se agregó solo en flujoFondos y
// anular una venta en revisión reintegraba stock que nunca se había descontado.

const normalizeStatus = (value) => String(value || "").trim().toLowerCase();

/**
 * Reintegra el stock de los items de la venta en la sucursal de origen y deja
 * el movimiento de kardex correspondiente.
 */
async function restoreSaleStock({ sale, saleItems, organizationId }) {
  const restorableCandidates = (saleItems || []).filter(
    (item) => item?.product_id && Number(item?.quantity || 0) > 0,
  );

  if (restorableCandidates.length === 0) {
    return { restoredCount: 0 };
  }

  const productIds = Array.from(
    new Set(restorableCandidates.map((item) => item.product_id)),
  );
  const { data: products, error: productsError } = await supabase
    .from("products")
    .select("id, product_type, stock_tracking, controla_vencimiento")
    .in("id", productIds);

  if (productsError) throw productsError;

  const productTypeById = new Map(
    (products || []).map((product) => [product.id, product.product_type]),
  );

  // Talles y colores: los productos con stock por talle se reponen a la
  // variante vendida (fn_mover_stock); el resto sigue el camino de siempre.
  const variantTrackedIds = new Set(
    (products || []).filter((product) => product.stock_tracking === "variant").map((product) => product.id),
  );
  // Si hubo cambios de talle sobre estas líneas, lo que vuelve es el talle que
  // el cliente se llevó (ver redistribuirPorCambiosTalle).
  let candidatosReposicion = restorableCandidates;
  const lineasConTalle = restorableCandidates.filter((item) => variantTrackedIds.has(item.product_id));
  if (lineasConTalle.length > 0) {
    const cambios = await fetchCambiosTalle(supabase, lineasConTalle.map((item) => item.id));
    if (cambios.length > 0) {
      candidatosReposicion = [
        ...restorableCandidates.filter((item) => !variantTrackedIds.has(item.product_id)),
        ...redistribuirPorCambiosTalle(lineasConTalle.map((item) => ({ ...item, sale_item_id: item.id })), cambios),
      ];
    }
  }
  const { variantItems, legacyItems } = splitVariantItems({
    items: candidatosReposicion,
    variantTrackedIds,
  });

  // Filtra filas padre de combo (formato nuevo y legacy) y agrega por producto
  // (un combo puede repetir el mismo componente en varias líneas).
  const quantityByProduct = buildRestorableQuantityMap({
    items: legacyItems,
    productTypeById,
  });

  const saleShortId = String(sale.id).slice(0, 8).toUpperCase();
  let restoredCount = 0;

  // Vencimientos y lotes: estos productos vuelven por la base a los mismos lotes
  // de los que salió la venta (si la empresa no usa vencimientos, la base hace
  // un movimiento común con el mismo resultado).
  const lotTrackedIds = new Set(
    (products || [])
      .filter((product) => product.controla_vencimiento && product.stock_tracking !== "variant")
      .map((product) => product.id),
  );

  if (variantItems.length > 0) {
    const { restored } = await restoreVariantStock({
      supabase,
      organizationId,
      siteId: sale.site_id,
      items: variantItems,
      motivo: `Anulación venta #${saleShortId}.`,
    });
    restoredCount += restored;
  }

  for (const [productId, quantity] of quantityByProduct.entries()) {
    if (quantity <= 0) continue;

    if (lotTrackedIds.has(productId)) {
      await devolverVentaALotes({
        supabase,
        organizationId,
        productId,
        siteId: sale.site_id,
        cantidad: quantity,
        saleId: sale.id,
        motivo: `Anulación venta #${saleShortId}.`,
        refTipo: "anulacion_venta",
      });
      restoredCount += 1;
      continue;
    }

    const { data: stockRow, error: stockError } = await supabase
      .from("stock_levels")
      .select("id, quantity")
      .eq("organization_id", organizationId)
      .eq("product_id", productId)
      .eq("site_id", sale.site_id)
      .maybeSingle();

    if (stockError) throw stockError;

    if (stockRow?.id) {
      const { error: updateError } = await supabase
        .from("stock_levels")
        .update({
          quantity: Number(stockRow.quantity || 0) + quantity,
          updated_at: new Date().toISOString(),
        })
        .eq("id", stockRow.id);

      if (updateError) throw updateError;
    } else {
      const { error: insertError } = await supabase.from("stock_levels").insert([
        {
          organization_id: organizationId,
          product_id: productId,
          site_id: sale.site_id,
          quantity,
          min_stock: 0,
        },
      ]);

      if (insertError) throw insertError;
    }

    const { error: movementError } = await supabase
      .from("movimientos_stock")
      .insert([
        {
          organization_id: organizationId,
          producto_id: productId,
          tipo: "Ingreso",
          cantidad: quantity,
          motivo: `Anulación venta #${saleShortId}.`,
          sitio_origen: null,
          sitio_destino: sale.site_id,
        },
      ]);

    if (movementError) throw movementError;
    restoredCount += 1;
  }

  return { restoredCount };
}

/**
 * Desactiva los movimientos financieros de la venta y, si el banco ya fue
 * acreditado (saldo_resultante != null, mismo criterio que bankSettlements),
 * revierte el saldo del banco.
 */
async function revertSaleFinancialMovements({ sale, organizationId }) {
  const { data: movements, error } = await supabase
    .from("movimientos_financieros")
    .select("id, bank_id, tipo, monto, saldo_resultante, descripcion, is_active")
    .eq("organization_id", organizationId)
    .eq("documento_tipo", "Venta")
    .eq("documento_id", sale.id);

  if (error) throw error;

  // Filtramos en JS (no con .neq) para no excluir filas con is_active = NULL.
  const activeMovements = (movements || []).filter(
    (movement) => movement?.is_active !== false,
  );

  let revertedBankCount = 0;

  for (const movement of activeMovements) {
    const amount = Number(movement?.monto || 0);
    const wasSettledToBank =
      Boolean(movement?.bank_id) && movement?.saldo_resultante != null;

    if (wasSettledToBank && amount > 0) {
      const { data: bankData, error: bankError } = await supabase
        .from("banks")
        .select("current_balance")
        .eq("id", movement.bank_id)
        .single();

      if (bankError) throw bankError;

      const isIngreso = normalizeStatus(movement.tipo) === "ingreso";
      const currentBalance = Number(bankData?.current_balance || 0);
      const restoredBalance = isIngreso
        ? currentBalance - amount
        : currentBalance + amount;

      const { error: bankUpdateError } = await supabase
        .from("banks")
        .update({ current_balance: restoredBalance })
        .eq("id", movement.bank_id);

      if (bankUpdateError) throw bankUpdateError;
      revertedBankCount += 1;
    }

    // Soft delete (mismo patrón que la anulación de cobros en Tesorería):
    // monto en 0 para que ningún agregado lo sume, is_active=false para que
    // processPendingBankSettlements nunca lo acredite a futuro.
    const { error: movementUpdateError } = await supabase
      .from("movimientos_financieros")
      .update({
        is_active: false,
        monto: 0,
        descripcion: `(ANULADO) ${movement.descripcion || ""}`.trim(),
      })
      .eq("id", movement.id);

    if (movementUpdateError) throw movementUpdateError;
  }

  return { deactivatedCount: activeMovements.length, revertedBankCount };
}

/**
 * Anula una venta revirtiendo TODOS sus efectos: estado, cuenta corriente,
 * receivable, stock, libro diario y banco.
 *
 * @returns {Promise<{applied?: boolean, skipped?: boolean, reason?: string,
 *   stock?: {restoredCount: number},
 *   finance?: {deactivatedCount: number, revertedBankCount: number}}>}
 */
export async function annulSaleWithReversal({
  saleId,
  organizationId,
  movementType = "Venta POS",
}) {
  if (!saleId || !organizationId) {
    return { skipped: true, reason: "missing_context" };
  }

  const [saleResponse, itemsResponse] = await Promise.all([
    supabase
      .from("sales")
      .select("id, client_id, site_id, total, status, active, invoice_status, afecta_stock")
      .eq("organization_id", organizationId)
      .eq("id", saleId)
      .maybeSingle(),
    supabase.from("sale_items").select("*").eq("sale_id", saleId),
  ]);

  if (saleResponse.error) throw saleResponse.error;
  if (itemsResponse.error) throw itemsResponse.error;

  const sale = saleResponse.data;
  if (!sale?.id) {
    return { skipped: true, reason: "sale_not_found" };
  }

  // Venta con el cobro de terminal sin confirmar: el POS cortó ANTES de descontar
  // stock y de asentar caja/diario, así que se marca anulada pero NO se revierte
  // nada (revertir reintegraría un stock que nunca se restó). Va antes del guard
  // de idempotencia porque este estado sí requiere el update.
  if (normalizeStatus(sale.status) === SALE_STATUS_REVISION) {
    const { data: rows, error } = await supabase
      .from("sales")
      .update({ active: false, status: "anulada", invoice_status: "anulada" })
      .eq("id", sale.id)
      .eq("active", true)
      .select("id");
    if (error) throw error;
    if (!rows || rows.length === 0) {
      return { skipped: true, reason: "already_annulled" };
    }
    return { applied: true, reason: "revision_sin_reversion", stock: null, finance: null };
  }

  // Idempotencia: si ya está anulada no repetimos reversiones (duplicarían
  // stock y saldos de banco).
  if (
    sale.active === false ||
    CANCELLED_SALE_STATUSES.has(normalizeStatus(sale.status))
  ) {
    return { skipped: true, reason: "already_annulled" };
  }

  // 1. Marcar la venta como anulada PRIMERO: si algo de lo que sigue falla,
  // preferimos una venta anulada con reversión parcial (visible y corregible)
  // antes que una venta activa con stock ya reintegrado.
  const { data: annulledRows, error: saleUpdateError } = await supabase
    .from("sales")
    .update({ active: false, status: "anulada", invoice_status: "anulada" })
    .eq("id", sale.id)
    .eq("active", true)
    .select("id");

  if (saleUpdateError) throw saleUpdateError;

  // Guard TOCTOU (bug V5): el update condicional a active=true es el "claim"
  // atómico. Si otra anulación concurrente (doble click, o Historial + Detalle a
  // la vez) ganó la carrera entre el chequeo de arriba y este update, no afecta
  // filas -> abortamos sin repetir la reversión de stock/banco.
  if (!annulledRows || annulledRows.length === 0) {
    return { skipped: true, reason: "already_annulled" };
  }

  // 2. Cuenta corriente + receivable (comportamiento que ya existía).
  if (sale.client_id) {
    await syncSaleClientDebit({
      saleId: sale.id,
      organizationId,
      clientId: sale.client_id,
      totalAmount: sale.total,
      movementType,
      observations: `Anulacion de venta #${String(sale.id).slice(0, 8)}.`,
      shouldApply: false,
    });
  }
  await deactivateSaleReceivable(sale.id);

  // 3. Stock + kardex. Una venta administrativa cargada sin descontar stock
  // (afecta_stock = false) no reintegra: sumaria mercaderia que nunca salio.
  const stock = sale.afecta_stock === false
    ? { restoredCount: 0, skipped: "venta_sin_descuento_de_stock" }
    : await restoreSaleStock({
        sale,
        saleItems: itemsResponse.data || [],
        organizationId,
      });

  // 4. Libro diario + banco.
  const finance = await revertSaleFinancialMovements({ sale, organizationId });

  // 5. Devolución en la pasarela. Si la venta se cobró con una terminal Point,
  // anularla en Impulza sin devolver deja la plata cobrada en Mercado Pago y el
  // comercio se entera recién en el resumen.
  //
  // Best-effort a propósito: la anulación local YA ocurrió y es lo correcto
  // aunque la devolución falle (puede fallar por red, por plazo vencido o
  // porque MP exige hacerla a mano). Si falla, se devuelve el detalle para que
  // la pantalla avise, en vez de romper la anulación entera.
  let refund = { attempted: false };

  // Solo si esta venta tiene un cobro de pasarela efectivamente procesado. Sin
  // este chequeo, TODA anulación (incluidas las de efectivo, que son la mayoría)
  // pagaba un round-trip a una edge function que no tenía nada que hacer.
  const { data: cobros } = await supabase
    .from("sale_gateway_payments")
    .select("id")
    .eq("organization_id", organizationId)
    .eq("sale_id", sale.id)
    .eq("status", "processed")
    .limit(1);

  if (cobros && cobros.length > 0) {
    try {
      // Timeout propio: sin esto, una edge function lenta deja la anulación
      // —que YA se aplicó en la base— con el spinner girando.
      const refundData = await Promise.race([
        supabase.functions
          .invoke("mp-point-refund", { body: { organizationId, saleId: sale.id } })
          .then((r) => {
            if (r.error || r.data?.error) throw new Error(r.error?.message || r.data?.error);
            return r.data;
          }),
        new Promise((_, reject) => setTimeout(
          () => reject(new Error("Tiempo de espera agotado al devolver el cobro.")),
          20000,
        )),
      ]);
      refund = { attempted: true, ...refundData };
    } catch (refundErr) {
      console.error("No se pudo devolver el cobro con terminal:", refundErr);
      refund = {
        attempted: true,
        refunded: false,
        error: refundErr?.message || "No se pudo devolver el cobro en Mercado Pago.",
      };
    }
  }

  return { applied: true, stock, finance, refund };
}
